/**
 * LaterProjectsDndList — two-group DnD list for Later Projects view.
 *
 * Renders "Upcoming" and "Someday" sections in a unified DndContext so
 * projects can be dragged between groups:
 *   - Upcoming → Someday: keep startAt, set startAtType = Someday
 *   - Someday → Upcoming: set startAt = tomorrow, startAtType = Default
 *
 * Each group has its own SortableContext + DroppableGroup for cross-group
 * collision detection. Reuses SortableTaskRow → TaskItem for rendering.
 */

import { useState, useMemo, useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { createPortal } from "react-dom"
import { Capacitor } from "@capacitor/core"
import {
  DndContext,
  DragOverlay,
  closestCenter,
  MouseSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { ListDragSensor } from "@/features/dnd/ListDragSensor"
import { DroppableGroup } from "@/features/dnd/DroppableGroup"
import { SortableTaskRow } from "@/features/dnd/SortableTaskRow"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { useTaskActions } from "@/features/tasks/hooks/useTaskActions"
import { useActiveTask } from "@/features/task-detail/hooks/useActiveTask"
import { useViewContext } from "@/app/stores/viewContext"
import { scheduleTask } from "@/db/operations"
import { StartAtType, StartAtSubType, ObjectType } from "@/db/schema"
import { ROUTES } from "@/config/routes"
import { contentZoomModifier } from "@/features/dnd/zoomModifier"
import { hapticLight, hapticMedium } from "@/shared/utils/platform/haptics"
import { handleSidebarDrop } from "@/features/dnd/sidebarDropHandler"
import type { ViewItem } from "@/features/views/hooks/useViewData"
import { computeGroupPositions } from "@/features/views/utils/groupPositions"
import dayjs from "dayjs"
import ChevronRightIcon from "@/shared/icons/totodoicons/stroke/chevron-right.svg?react"
import { cn } from "@/shared/utils/general/cn"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const UPCOMING_GROUP = "later-upcoming"
const SOMEDAY_GROUP = "later-someday"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LaterProjectsDndListProps {
  /** Upcoming projects (startAtType = Default, future date) */
  upcomingItems: ViewItem[]
  /** Someday projects (startAtType = Someday) */
  somedayItems: ViewItem[]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LaterProjectsDndList({
  upcomingItems,
  somedayItems,
}: LaterProjectsDndListProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { completeTask, uncompleteTask } = useTaskActions()
  const { activeTaskId, isEditingTitle, openTask, closeTask } = useActiveTask()
  const setDragging = useViewContext((s) => s.setDragging)

  // For projects/lists, navigate to the container route instead of opening task detail
  const allItems = useMemo(
    () => [...upcomingItems, ...somedayItems],
    [upcomingItems, somedayItems],
  )

  const handleOpen = useCallback(
    (id: string) => {
      const item = allItems.find((vi) => vi.task.id === id)
      if (item) {
        const type = item.task.type
        if (type === ObjectType.Project) {
          navigate(ROUTES.PROJECT(id))
          return
        }
        if (type === ObjectType.List) {
          navigate(ROUTES.LIST(id))
          return
        }
      }
      openTask(id)
    },
    [allItems, navigate, openTask],
  )

  // Compute group positions for border-radius
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const upcomingPos = useMemo(
    () => computeGroupPositions(upcomingItems),
    [upcomingItems],
  )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const somedayPos = useMemo(
    () => computeGroupPositions(somedayItems),
    [somedayItems],
  )

  // ── Local DnD state ───────────────────────────────────────────────────
  const [activeId, setActiveId] = useState<string | null>(null)
  const [somedayCollapsed, setSomedayCollapsed] = useState(false)

  // Track which group the dragged item originated from
  const sourceGroupRef = useRef<string | null>(null)

  // Memoize item ID arrays for SortableContext
  const upcomingIds = useMemo(
    () => upcomingItems.map((vi) => vi.task.id ?? "no-id"),
    [upcomingItems],
  )
  const somedayIds = useMemo(
    () => somedayItems.map((vi) => vi.task.id ?? "no-id"),
    [somedayItems],
  )

  // Find the active item for DragOverlay
  const activeItem = useMemo(() => {
    if (!activeId) return null
    return (
      upcomingItems.find((vi) => vi.task.id === activeId) ??
      somedayItems.find((vi) => vi.task.id === activeId) ??
      null
    )
  }, [activeId, upcomingItems, somedayItems])

  // ── Sensors ───────────────────────────────────────────────────────────
  // CRITICAL FIX for macOS Capacitor: Desktop apps must use MouseSensor only.
  // ListDragSensor uses TouchEvents which bypass auto-scroll in DnD Kit.
  // On macOS, use only MouseSensor (supports auto-scroll).
  // On mobile, use both MouseSensor and ListDragSensor.
  const isMacOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "macOS"
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    ...(isMacOS ? [] : [useSensor(ListDragSensor, {
      activationConstraint: { delay: 250, tolerance: 3 },
    })]),
  )

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = event.active.id as string
      setActiveId(id)
      setDragging(true, id)
      hapticLight()

      // Track source group
      if (upcomingIds.includes(id)) {
        sourceGroupRef.current = UPCOMING_GROUP
      } else if (somedayIds.includes(id)) {
        sourceGroupRef.current = SOMEDAY_GROUP
      }
    },
    [setDragging, upcomingIds, somedayIds],
  )

  const handleDragOver = useCallback((_event: DragOverEvent) => {
    // For now, no visual cross-group transfer — the DragOverlay handles
    // the visual feedback. Future: could move item between groups in local state.
  }, [])

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const id = event.active.id as string
      setActiveId(null)
      setDragging(false)
      hapticMedium()

      // ── Sidebar drop ────────────────────────────────────────────────
      const sidebarTarget = useViewContext.getState().sidebarDropTarget
      if (sidebarTarget) {
        await handleSidebarDrop(sidebarTarget, [id])
        return
      }

      // ── Cross-group drop ────────────────────────────────────────────
      const over = event.over
      if (!over) return

      const overId = over.id as string
      const overData = over.data.current

      // Determine target group
      let targetGroup: string | null = null
      if (overData?.type === "group") {
        targetGroup = overData.groupId as string
      } else if (upcomingIds.includes(overId)) {
        targetGroup = UPCOMING_GROUP
      } else if (somedayIds.includes(overId)) {
        targetGroup = SOMEDAY_GROUP
      }

      if (!targetGroup || !sourceGroupRef.current) return

      // Cross-group move
      if (sourceGroupRef.current !== targetGroup) {
        if (targetGroup === SOMEDAY_GROUP) {
          // Upcoming → Someday: keep existing startAt, change type to Someday
          const item = upcomingItems.find((vi) => vi.task.id === id)
          const existingStartAt = item?.startAtData?.startAt
          await scheduleTask(
            id,
            existingStartAt ?? dayjs().valueOf().toString(),
            StartAtType.Someday,
            StartAtSubType.Default,
          )
        } else {
          // Someday → Upcoming: set startAt to tomorrow, type = Default
          const tomorrow = dayjs()
            .add(1, "day")
            .startOf("day")
            .valueOf()
            .toString()
          await scheduleTask(
            id,
            tomorrow,
            StartAtType.Default,
            StartAtSubType.Default,
          )
        }
      }
      // Within same group: no reorder needed for now (small lists, sorted by date)

      sourceGroupRef.current = null
    },
    [setDragging, upcomingItems, upcomingIds, somedayIds],
  )

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
    setDragging(false)
    sourceGroupRef.current = null
  }, [setDragging])

  // ── Render ────────────────────────────────────────────────────────────
  // macOS WKWebView scroll-delta fix is patched in App.tsx via patchWindowScrollForMacOS()
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={{ enabled: true }}
    >
      {/* Upcoming section */}
      {upcomingItems.length > 0 && (
        <DroppableGroup id={UPCOMING_GROUP}>
          <SortableContext
            items={upcomingIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col">
              {upcomingItems.map((item, idx) => {
                const taskId = item.task.id ?? ""
                return (
                  <SortableTaskRow
                    key={taskId}
                    item={item}
                    onComplete={completeTask}
                    onUncomplete={uncompleteTask}
                    isActive={taskId === activeTaskId}
                    editTitle={taskId === activeTaskId && isEditingTitle}
                    onOpen={handleOpen}
                    onClose={closeTask}
                    isFirstInGroup={upcomingPos.firsts[idx]}
                    isLastInGroup={upcomingPos.lasts[idx]}
                  />
                )
              })}
            </div>
          </SortableContext>
        </DroppableGroup>
      )}

      {/* Someday section — collapsible */}
      {somedayItems.length > 0 && (
        <DroppableGroup id={SOMEDAY_GROUP}>
          <button
            type="button"
            onClick={() => setSomedayCollapsed((c) => !c)}
            className="flex w-full items-center gap-2 border-t border-border px-4 pb-1 pt-4 text-left"
          >
            <ChevronRightIcon
              width={12}
              height={12}
              className={cn(
                "text-muted-foreground transition-transform",
                !somedayCollapsed && "rotate-90",
              )}
            />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("Someday")}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {somedayItems.length}
            </span>
          </button>
          {!somedayCollapsed && (
            <SortableContext
              items={somedayIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col">
                {somedayItems.map((item, idx) => {
                  const taskId = item.task.id ?? ""
                  return (
                    <SortableTaskRow
                      key={taskId}
                      item={item}
                      onComplete={completeTask}
                      onUncomplete={uncompleteTask}
                      isActive={taskId === activeTaskId}
                      editTitle={taskId === activeTaskId && isEditingTitle}
                      onOpen={handleOpen}
                      onClose={closeTask}
                      isFirstInGroup={somedayPos.firsts[idx]}
                      isLastInGroup={somedayPos.lasts[idx]}
                    />
                  )
                })}
              </div>
            </SortableContext>
          )}
        </DroppableGroup>
      )}

      {/* Drag overlay — renders the dragged item in a portal */}
      {createPortal(
        <DragOverlay dropAnimation={null} modifiers={[contentZoomModifier]}>
          {activeItem && (
            <div className="rounded-lg bg-background shadow-lg ring-1 ring-border">
              <TaskItem
                task={activeItem.task}
                onComplete={() => {}}
                onUncomplete={() => {}}
                startAtData={activeItem.startAtData}
              />
            </div>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}
